// The API limits how fast one user can call it (DEV-1754). Over a limit it
// answers 429 with Retry-After, in seconds, and does nothing else, so the
// request is safe to send again once the wait is over. apiRequest does that
// for every API call, so no caller and no React Query retry handles a 429.

import axios from 'axios'
import {
  isOrganizationContextMismatch,
  ORGANIZATION_CONTEXT_HEADER
} from './organization-context'

const RATE_LIMIT_RETRIES = 3
const FALLBACK_WAIT_MS = 1000
const LONGEST_WAIT_MS = 30_000

type ErrorWithResponse = {
  response?: {
    status?: number
    headers?: { get?: (name: string) => unknown; [name: string]: unknown }
  }
}

// True when the API refused the request for coming too fast.
export const isTooManyRequests = (error: unknown): boolean =>
  (error as ErrorWithResponse)?.response?.status === 429

// How long the API asked us to wait, in milliseconds.
const retryAfterMs = (error: unknown): number => {
  const headers = (error as ErrorWithResponse)?.response?.headers
  const value = headers?.get?.('retry-after') ?? headers?.['retry-after']
  const seconds =
    typeof value === 'string' && value !== '' ? Number(value) : NaN
  return Number.isFinite(seconds) && seconds >= 0
    ? Math.min(seconds * 1000, LONGEST_WAIT_MS)
    : FALLBACK_WAIT_MS
}

// The organization this tab acts as. Another tab's switch changes it.
const viewedOrganization = (): unknown =>
  axios.defaults?.headers?.common?.[ORGANIZATION_CONTEXT_HEADER]

// Sends a request, and sends it again after the API's wait each time it is
// rate limited, up to three times. It stops if the tab switched organization
// during a wait: the request sent again would act for the new organization,
// not the one the user acted in.
export const withRateLimitRetry = async <T>(
  send: () => Promise<T>
): Promise<T> => {
  const organization = viewedOrganization()
  for (let failureCount = 0; ; failureCount++) {
    try {
      return await send()
    } catch (error) {
      if (!isTooManyRequests(error) || failureCount >= RATE_LIMIT_RETRIES) {
        throw error
      }
      await new Promise((resolve) => setTimeout(resolve, retryAfterMs(error)))
      if (viewedOrganization() !== organization) {
        throw error
      }
    }
  }
}

// Global query retry (wired in the app providers): any failure once, except a
// 429, which apiRequest already retried.
export const queryRetry = (failureCount: number, error: unknown): boolean =>
  !isTooManyRequests(error) && failureCount < 1

// Global mutation retry (wired in the app providers): retry once, but never a
// 429, which apiRequest already retried, and never a write rejected for a
// stale organization context — the 409 handler corrects the header, so a retry
// would silently land the write on the new organization, the exact
// misattribution the guard exists to prevent.
export const mutationRetry = (failureCount: number, error: unknown): boolean =>
  !isOrganizationContextMismatch(error) && queryRetry(failureCount, error)
